// Bulk PRINT PACK menu on the Spools tab. Spool-scoped actions
// operate on the row-checkbox selection (A4 packs via
// /reports/job/:id/print-pack.pdf, labels via /labels.pdf at
// 150x100mm). Job-scoped documents (the Master Job Card) need no
// selection, so the menu button itself stays enabled and only the
// selection-dependent items grey out.
import { useEffect, useRef, useState } from "react";
import { apiDownload } from "../../../../lib/api";

interface Props {
  jobId: number;
  selectedSpoolIds: number[];
  jobNumber: string;
}

type A4Kind = "job-card" | "cut-list" | "fittings-list" | "cut-fittings-list" | "time-sheet";

interface Action {
  key: string;
  label: string;
  icon: string;
  // kinds is empty for the labels + master actions, which use their
  // own endpoints. A4 actions list their kinds and post to
  // print-pack.pdf.
  kinds: A4Kind[];
  endpoint: "print-pack" | "labels" | "master";
  bold?: boolean;
  hint?: string;
}

const MASTER_ACTION: Action = {
  key: "master-job-card",
  label: "Master Job Card",
  icon: "🗂",
  kinds: [],
  endpoint: "master",
  bold: true,
  hint: "Order summary + full spool list — whole job, no selection needed",
};

const ACTIONS: Action[] = [
  {
    key: "jobpack",
    label: "Full Job Pack",
    icon: "📦",
    // Cut + Fittings live on a single half-and-half sheet so the
    // pack stays one Job Card → one combined Cut/Fittings → one
    // Time Sheet per spool.
    kinds: ["job-card", "cut-fittings-list", "time-sheet"],
    endpoint: "print-pack",
    bold: true,
    hint: "Job Card · Cut + Fittings · Time",
  },
  { key: "job-card", label: "A4 Job Cards", icon: "📄", kinds: ["job-card"], endpoint: "print-pack" },
  { key: "cut-fittings-list", label: "Cut + Fittings Sheets", icon: "✂", kinds: ["cut-fittings-list"], endpoint: "print-pack" },
  { key: "cut-list", label: "Cut Lists (full page)", icon: "✂", kinds: ["cut-list"], endpoint: "print-pack" },
  { key: "fittings-list", label: "Fittings Lists (full page)", icon: "⚙", kinds: ["fittings-list"], endpoint: "print-pack" },
  { key: "time-sheet", label: "Time Sheets", icon: "⏱", kinds: ["time-sheet"], endpoint: "print-pack" },
  { key: "labels", label: "100×150mm Labels", icon: "🏷", kinds: [], endpoint: "labels" },
];

export function BulkPrintMenu({ jobId, selectedSpoolIds, jobNumber }: Props) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const noSelection = selectedSpoolIds.length === 0;

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const run = async (action: Action) => {
    setBusy(action.key);
    try {
      if (action.endpoint === "master") {
        await apiDownload(
          `/api/tools/production-schedule/reports/job/${jobId}/master-job-card.pdf`,
          `${jobNumber}_master-job-card.pdf`,
        );
        return;
      }
      const query = new URLSearchParams();
      query.set("spools", selectedSpoolIds.join(","));
      if (action.endpoint === "print-pack") query.set("kinds", action.kinds.join(","));
      const path = action.endpoint === "labels" ? "labels.pdf" : "print-pack.pdf";
      await apiDownload(
        `/api/tools/production-schedule/reports/job/${jobId}/${path}?${query.toString()}`,
        `${jobNumber}_${action.key}_${selectedSpoolIds.length}sp.pdf`,
      );
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      window.alert(`${action.label} failed: ${msg}`);
    } finally {
      setBusy(null);
      setOpen(false);
    }
  };

  return (
    <div ref={wrapRef} style={{ position: "relative", display: "inline-block" }}>
      <button
        type="button"
        className="btn-sm"
        title="Print documents for this job"
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        style={{
          background: "var(--bsp-blue)",
          color: "#fff",
          borderColor: "var(--bsp-blue)",
        }}
      >
        🖨 Print pack ▾
      </button>
      {open && (
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            position: "absolute",
            right: 0,
            top: "calc(100% + 4px)",
            background: "var(--bsp-surface)",
            border: "1px solid var(--bsp-border)",
            borderRadius: 4,
            boxShadow: "var(--bsp-shadow-pop)",
            padding: 4,
            zIndex: 200,
            minWidth: 260,
          }}
        >
          <SectionHeader>Job documents</SectionHeader>
          <ActionItem
            action={MASTER_ACTION}
            busy={busy === MASTER_ACTION.key}
            onRun={() => void run(MASTER_ACTION)}
          />
          <SectionHeader>Job pack — selected spools</SectionHeader>
          {ACTIONS.slice(0, 1).map((a) => (
            <ActionItem
              key={a.key}
              action={a}
              busy={busy === a.key}
              disabled={noSelection}
              title={noSelection ? "Select spools to enable" : undefined}
              onRun={() => void run(a)}
            />
          ))}
          <SectionHeader>Single document type — selected spools</SectionHeader>
          {ACTIONS.slice(1).map((a) => (
            <ActionItem
              key={a.key}
              action={a}
              busy={busy === a.key}
              disabled={noSelection}
              title={noSelection ? "Select spools to enable" : undefined}
              onRun={() => void run(a)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function SectionHeader({ children }: { children: string }) {
  return (
    <div style={{
      fontSize: 9,
      fontWeight: 700,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      color: "var(--bsp-fg-muted)",
      padding: "8px 10px 4px",
    }}>{children}</div>
  );
}

function ActionItem({
  action,
  onRun,
  busy,
  disabled,
  title,
}: {
  action: Action;
  onRun?: () => void;
  busy?: boolean;
  disabled?: boolean;
  title?: string;
}) {
  const handleClick = disabled || busy || !onRun ? undefined : onRun;
  return (
    <div
      role="button"
      tabIndex={disabled ? -1 : 0}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (handleClick && (e.key === "Enter" || e.key === " ")) handleClick();
      }}
      title={title}
      style={{
        padding: "7px 10px",
        cursor: disabled ? "not-allowed" : "pointer",
        borderRadius: 2,
        fontSize: 12,
        color: disabled ? "var(--bsp-fg-muted)" : "var(--bsp-fg)",
        opacity: disabled ? 0.55 : 1,
      }}
      onMouseOver={(e) => {
        if (!disabled) e.currentTarget.style.background = "var(--bsp-blue-pale)";
      }}
      onMouseOut={(e) => { e.currentTarget.style.background = ""; }}
    >
      {busy ? (
        "Downloading…"
      ) : (
        <>
          <strong style={{ fontWeight: action.bold ? 700 : 600 }}>
            {action.icon} {action.label}
          </strong>
          {action.hint && (
            <div style={{ fontSize: 10, color: "var(--bsp-fg-muted)", marginTop: 1 }}>{action.hint}</div>
          )}
        </>
      )}
    </div>
  );
}
